import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Gem, Award, MapPin } from "lucide-react";
import { Reveal } from "@/components/common/Reveal";
import { api } from "@/lib/api";

const CHIP_ICONS = [Gem, Award, MapPin];

const DEFAULT_CAR = {
  eyebrow: "Featured this week",
  title: "The Lamborghini Urus",
  description:
    "A full interior detail on Rockcliffe Park's most exotic daily driver. Every panel of Alcantara, every hex-stitched leather seat and carbon accent restored to concours condition — right in the owner's driveway.",
  image: "/gallery/img_6729.webp",
  badge: "Detailed in-driveway",
  chips: ["Exotic supercar", "Full Interior Detail", "Rockcliffe Park"],
};

export default function FeaturedShowcase() {
  const [car, setCar] = useState(DEFAULT_CAR);

  useEffect(() => {
    api.get("/featured").then(({ data }) => { if (data?.car) setCar(data.car); }).catch(() => {});
  }, []);

  return (
    <section data-testid="featured-showcase" className="relative overflow-hidden bg-brand-charcoal text-white">
      <div className="pointer-events-none absolute -left-40 top-1/2 h-96 w-96 -translate-y-1/2 rounded-full bg-brand-red/20 blur-3xl" />
      <div className="container-rv relative py-24 lg:py-32">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal y={40}>
            <p className="overline text-brand-red">{car.eyebrow || "Featured this week"}</p>
            <h2 className="mt-4 font-serif text-4xl font-bold leading-tight sm:text-5xl">{car.title}</h2>
            <p className="mt-6 max-w-md leading-relaxed text-brand-silver">{car.description}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {(car.chips || []).map((label, i) => {
                const Icon = CHIP_ICONS[i] || Gem;
                return (
                  <span key={label} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-brand-silver">
                    <Icon className="h-3.5 w-3.5 text-brand-red" /> {label}
                  </span>
                );
              })}
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/gallery" className="btn-red" data-testid="featured-gallery-btn">
                See our work <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/book"
                data-testid="featured-book-btn"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-white hover:text-brand-charcoal"
              >
                Book your detail
              </Link>
            </div>
          </Reveal>

          <Reveal y={40} delay={0.1}>
            <div className="group relative">
              <div className="pointer-events-none absolute -inset-3 rounded-3xl border border-white/10" />
              <div className="relative overflow-hidden rounded-2xl">
                <img
                  key={car.image}
                  src={car.image}
                  alt={`${car.title} detailed by Reverence Autos`}
                  className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-brand-charcoal/60 via-transparent to-transparent" />
                {car.badge && (
                  <div className="absolute bottom-5 left-5 rounded-full bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-wider backdrop-blur-md">
                    {car.badge}
                  </div>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
